import { PlatformContext } from 'PlatformContext';
import React, { useContext, useEffect, useRef, useState } from 'react'
import useArticle from 'graphql/hooks/useArticle';
import useArticles from 'graphql/hooks/useArticles';
import Markdown from 'react-markdown';
import { Article, ArticleSearchData, ArticleSearchVars, GET_ARTICLE_SEARCH, SearchResult } from 'graphql/ArticleQueries';
import { useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import { EledoArticleVoteBox } from './EledoArticleVoteBox';
import { ArticleHistory } from 'hooks/useArticleHistory';
import remarkGfm from "remark-gfm";
import styles from './EledoArticleView.module.scss';
import classnames from 'classnames';
import { visit } from 'unist-util-visit'
import { Tracker } from 'Tracker';
import { AuthContext } from 'AuthContext';

const EledoArticleView = ({root, path, history, scrollableRef}: {root: string, path?: string, history: ArticleHistory, scrollableRef?: React.RefObject<HTMLDivElement>}) => {
  const auth = useContext(AuthContext);
  const platform = useContext(PlatformContext);
  const [searchMode, setSearchMode] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<[SearchResult]>()
  
  const { loading: articleLoading, article } = useArticle({id: history.currentArticleId, path: history.currentPath})
  const [ articleSearch ] = useLazyQuery<ArticleSearchData, ArticleSearchVars>(GET_ARTICLE_SEARCH)
  
  const {reset, register, handleSubmit} = useForm<ArticleSearchVars>({mode: 'onSubmit'})

  useEffect(() => {
    article?.path &&
      Tracker.pageView(article.path, {
        id: auth.user?.id || null,
        level: auth.level
      });

      scrollableRef?.current?.scrollTo(0, 0);

  }, [article, auth.level, auth.user?.id])

  const articleNavigate = (articleId?: string, href?: string) => {
    setSearchMode(false)
    reset()

    if(articleId || href?.startsWith('/')) {
      history.navigate(articleId, href)
    } else if(href?.startsWith('https://')) {
      console.log("ignored link", href)
    } else {
      console.log("ignored link", href)
    }
  }

  const search = (data: ArticleSearchVars) => {
    setSearchMode(true)
    articleSearch({variables: {searchTerm: data.searchTerm, root, platform: platform.platform}})
      .then(result => {
        setSearchResults(result.data?.articleSearch.results)
      })
  }

  const searchClose = () => {
    setSearchMode(false)
    reset()
  }

  const remarkArticleLinks = () => {
    return (tree: any) => {
      visit(tree, 'link', (link, index, parent) => {
        if (!parent || index == null) {
          return
        }
        
        const next = parent.children[index + 1]
        if ( next && next.type === 'text' ) {
          const match = next.value.match(/^\{([^}]+)\}/)
          
          if (match) {
            link.data = link.data || {}
            link.data.articleId = match[1]

            // This gets transferred to the HAST <a> element
            link.data.hProperties = {
              ...link.data.hProperties,
              'data-article-id': match[1]
            }
            
            // Remove the {id} from the following text node
            next.value = next.value.substring(match[0].length)
            
            // Remove the text node completely if nothing remains
            if (!next.value) {
              parent.children.splice(index + 1, 1)
            }
          }
        }
      })
    }
  }

  const remarkComponents = () => {
    return (tree: any) => {
      visit(tree, 'paragraph', (node, index, parent) => {
        if (!parent || index == null) {
          return
        }

        // Look for:
        //
        // :::col-50
        //
        // ...
        //
        // :::
        //
        // Since react-markdown has already parsed the markdown,
        // these markers will normally be represented as text nodes.

        const first = node.children?.[0]

        if (
          first &&
          first.type === 'text' &&
          first.value.match(/^:::([\w-]+)\s*$/)
        ) {
          const match = first.value.match(/^:::([\w-]+)\s*$/)
          const componentName = match[1]

          // Find the closing ::: paragraph
          for (let i = index + 1; i < parent.children.length; i++) {
            const candidate = parent.children[i]

            if (
              candidate.type === 'paragraph' &&
              candidate.children?.length === 1 &&
              candidate.children[0].type === 'text' &&
              candidate.children[0].value.trim() === ':::'
            ) {
              // Extract everything between the opening and closing marker
              const children = parent.children.slice(index + 1, i)

              const component = {
                type: 'div',
                name: componentName,
                children
              }

              parent.children.splice(
                index,
                i - index + 1,
                component
              )

              return //[visit.SKIP]
            }
          }
        }
      })
    }
  }



  return (
    <>
          <div key="breadcrumbs" className="breadcrumbs" style={{display: 'flex', flexDirection: 'row'}}>
            { !searchMode &&
              <div style={{flexGrow: 1}}>
                { article?.parents.map((p, index, array) => (
                  <span key={"crumb_" + index}>
                    <span className="crumb-text" onClick={() => articleNavigate(p. id, p.path)}>
                      {p.title}
                    </span>
                    { array.length !== index + 1 &&
                      <span className="sep">&nbsp;&nbsp;/&nbsp;&nbsp;</span>
                    }
                  </span>
                ))}
              </div>
            }
            { searchMode &&
              <div style={{width: '25%'}}>
                <span className="crumb-text" onClick={() => searchClose()}>
                  {"< Back"}
                </span>
              </div>
            }
            { !searchMode &&
              <div>
                { history.canGoBack &&
                  <button className="crumb-text" onClick={() => history.goBack()}>&lt; Back</button>
                }
                &nbsp;
                { history.canGoForward &&
                  <button className="crumb-text" onClick={() => history.goForward()}>&gt;</button>
                }
              </div>
            }
            <div>
              <div className="crumb-text">
                <form className={"w-form"} style={{marginBottom: 0}} onSubmit={handleSubmit(search)}>
                  <input placeholder='Search' className='w-input' style={{margin: '-8px 0px'}} {...register("searchTerm")} />
                </form>
              </div>
            </div>
          </div>
          { !articleLoading && article === undefined &&
            <h3>Article not found: {history.currentPath}</h3>
          }
          <div style={{display: 'flex', flexDirection: 'row', marginTop: '30px'}}>
            <div style={{width: '25%', padding: '0 15px', marginTop: '20px'}}>
              { !searchMode && article !== undefined &&
                <ArticleChildren article={article} onClick={articleNavigate}/>
              }
            </div>
            <div style={{width: '75%', padding: '0 15px'}}>
              <div style={{paddingBottom: '8rem', marginTop: '20px'}} className={styles.markdown}>
                { !searchMode && article !== undefined &&
                  <>
                    <Markdown
                      remarkPlugins={[
                        remarkArticleLinks,
                        //remarkComponents,
                        remarkGfm]}
                      components={{
                        a({children, className, node, href}) {
                          const articleId = node?.properties?.['data-article-id'] as string

                          return (<button onClick={(event) => {
                            event.preventDefault();
                            articleNavigate(articleId, href)
                          }}>{children}</button>)
                        },
                        // div: ({ node, children }) => {
                        //   return (
                        //     <div className={''}>
                        //       {children}
                        //     </div>
                        //   )
                        // }
                        // table(props) {
                        //   return (<table className={"table"}>{props.children}</table>)
                        // }
                      }}
                    >{article.markdown || article.content}</Markdown>

                    <hr/>
                    <EledoArticleVoteBox articleId={article.id}/>
                  </>
                }
                { searchMode && searchResults &&
                  <Markdown
                    components={{
                      a({children, className, node, href}) {
                        return (<button onClick={(event) => {event.preventDefault(); href && articleNavigate(href)}}>{children}</button>)
                      }
                    }}
                    remarkPlugins={[remarkGfm]}
                    >{searchResults.map(r => "### [" + r.title + "](" + r.url + ")\n" + (r.message || "")).join("\n")}</Markdown>
                }
              </div>
            </div>
          </div>
          {}
    </>
  )
}

const ArticleChildren = ({article, onClick}: {article: Article, onClick: (articleId: string, path: string) => void}) =>{
  const platform = useContext(PlatformContext)

  const [parentId, setParentId] = useState<string>(article.id)
  const { loading: articlesLoading, articles } = useArticles({parentId: parentId, platform: platform.platform})

  useEffect(() => {
    setParentId(article.id)
  }, [article])

  useEffect(() => {
    if(articlesLoading === false && articles && articles.length === 0){
      setParentId(article.parentId)
    }
  }, [article, articlesLoading, articles])

  return (
    <div className='menu-collapse collapse'>
      <div className={classnames('sidebar-card', styles.sidebar)}>
        { articles !== undefined && articles.sort((a, b) => (a.ordr || 0) - (b.ordr || 0)).map(article => (
          <button key={article.id} className='sidebar-link w-inline-block' onClick={(event) => {event.preventDefault(); onClick(article.id, article.path)}}>
            <div>{article.title}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default EledoArticleView